/**
 * Bella Christie Jewellery — Wishlist
 *
 * Storage: localStorage (primary) + cart attribute backup for session persistence.
 * Item shape: { id: String, handle: String, title: String, price: Number, image: String, url: String, badge: String }
 *
 * Web Components:
 *   <wishlist-button> — toggle heart on product cards + product page
 *   <wishlist-count>  — live count badge in header
 */

const BC_WISHLIST_KEY = 'bc_wishlist';

// ---------------------------------------------------------------------------
// WishlistManager — core read/write logic
// ---------------------------------------------------------------------------
class WishlistManager {
  static getAll() {
    try { return JSON.parse(localStorage.getItem(BC_WISHLIST_KEY) || '[]'); }
    catch { return []; }
  }

  static has(id) {
    return this.getAll().some(item => item.id === String(id));
  }

  static add(item) {
    const all = this.getAll();
    if (this.has(item.id)) return;
    all.push({ ...item, id: String(item.id) });
    this._save(all);
  }

  static remove(id) {
    this._save(this.getAll().filter(item => item.id !== String(id)));
  }

  static toggle(item) {
    this.has(item.id) ? this.remove(item.id) : this.add(item);
  }

  static count() {
    return this.getAll().length;
  }

  static _save(items) {
    localStorage.setItem(BC_WISHLIST_KEY, JSON.stringify(items));
    document.dispatchEvent(
      new CustomEvent('wishlist:updated', { detail: { items, count: items.length } })
    );
    this._syncToCart(items.map(i => i.id).join(','));
  }

  // Back up IDs to cart attributes so logged-in customers keep their list across browsers
  static async _syncToCart(ids) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _bc_wishlist: ids } }),
      });
    } catch {}
  }

  // On cold load: if localStorage is empty, try to restore IDs from cart attributes
  static async init() {
    if (this.getAll().length > 0) return;
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();
      const stored = cart.attributes?._bc_wishlist;
      if (!stored) return;
      const ids = stored.split(',').map(s => s.trim()).filter(Boolean);
      if (!ids.length) return;
      // Stored IDs only — fetch handles from product JSON to build rich items
      const fetches = ids.map(id =>
        fetch(`/search/suggest.json?q=${id}&resources[type]=product&resources[limit]=1`)
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
      );
      const results = await Promise.all(fetches);
      const items = [];
      results.forEach((result, i) => {
        const p = result?.resources?.results?.products?.[0];
        if (p) {
          items.push({
            id: String(ids[i]),
            handle: p.handle,
            title: p.title,
            price: p.price,
            image: p.image?.src || '',
            url: p.url,
          });
        }
      });
      if (items.length) {
        localStorage.setItem(BC_WISHLIST_KEY, JSON.stringify(items));
        document.dispatchEvent(
          new CustomEvent('wishlist:updated', { detail: { items, count: items.length } })
        );
      }
    } catch {}
  }
}

// Expose globally so wishlist page script can use it
window.WishlistManager = WishlistManager;
WishlistManager.init();

// ---------------------------------------------------------------------------
// <wishlist-button> — heart toggle on cards + product page
// ---------------------------------------------------------------------------
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this._id     = this.dataset.productId;
    this._handle = this.dataset.productHandle;
    this._title  = this.dataset.productTitle  || '';
    this._price  = parseInt(this.dataset.productPrice || '0', 10);
    this._image  = this.dataset.productImage  || '';
    this._url    = this.dataset.productUrl    || '';
    this._badge  = this.dataset.productBadge  || '';

    this._updateState();
    this.addEventListener('click', this._onClick.bind(this));
    document.addEventListener('wishlist:updated', () => this._updateState());
  }

  _onClick(e) {
    e.preventDefault();
    e.stopPropagation();
    WishlistManager.toggle({
      id:     this._id,
      handle: this._handle,
      title:  this._title,
      price:  this._price,
      image:  this._image,
      url:    this._url,
      badge:  this._badge,
    });
    this.classList.add('wishlist-button--pop');
    this.addEventListener('animationend', () => this.classList.remove('wishlist-button--pop'), { once: true });
  }

  _updateState() {
    const active = WishlistManager.has(this._id);
    this.classList.toggle('wishlist-button--active', active);
    this.setAttribute('aria-pressed', String(active));
    this.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Save to wishlist');
  }
}

customElements.define('wishlist-button', WishlistButton);

// ---------------------------------------------------------------------------
// <wishlist-count> — live count badge in header
// ---------------------------------------------------------------------------
class WishlistCount extends HTMLElement {
  connectedCallback() {
    this._update();
    document.addEventListener('wishlist:updated', () => this._update());
  }

  _update() {
    const count = WishlistManager.count();
    this.textContent = count > 0 ? String(count) : '';
    this.toggleAttribute('hidden', count === 0);
  }
}

customElements.define('wishlist-count', WishlistCount);
